import { useMemo } from 'react'
import { diffLines } from 'diff'
import { useTranslation } from 'react-i18next'
import { formatValue } from '../activity/ActivityEntry'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import { blockText, fieldRows, isMultiline, type FieldRow, type SyncChange } from '../../lib/ontology-sync'

export interface FieldDiffProps {
  changes: SyncChange[]
  /** Column of the before side. Omit it to paint a single column (a new item). */
  beforeLabel?: string
  afterLabel: string
  'data-testid'?: string
}

/**
 * FieldDiff paints an item's changes field by field (spec 2026-09-25-02): one
 * row per changed leaf, a locale map split per locale, a set change as chips,
 * and a multi-line value (a script, a JSON condition, a pipeline) as a line
 * diff. Below `md` each field stacks: its label, then the two values.
 */
export function FieldDiff({ changes, beforeLabel, afterLabel, 'data-testid': testId }: FieldDiffProps) {
  const { t } = useTranslation('types')
  const rows = useMemo(() => fieldRows(changes), [changes])
  const single = beforeLabel === undefined

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={testId}>
        {t('sync.no_field_changes')}
      </p>
    )
  }

  return (
    <div className="text-sm" data-testid={testId}>
      <div
        className={cn(
          'hidden border-b pb-1 text-xs font-medium text-muted-foreground md:grid md:gap-3',
          single ? 'md:grid-cols-[minmax(8rem,1fr)_3fr]' : 'md:grid-cols-[minmax(8rem,1fr)_2fr_2fr]',
        )}
      >
        <span>{t('sync.field')}</span>
        {!single && <span>{beforeLabel}</span>}
        <span>{afterLabel}</span>
      </div>
      <ul className="divide-y">
        {rows.map((row) => (
          <FieldDiffRow key={row.path} row={row} single={single} beforeLabel={beforeLabel} afterLabel={afterLabel} />
        ))}
      </ul>
    </div>
  )
}

function FieldDiffRow({
  row,
  single,
  beforeLabel,
  afterLabel,
}: {
  row: FieldRow
  single: boolean
  beforeLabel?: string
  afterLabel: string
}) {
  const isSet = row.added !== undefined || row.removed !== undefined
  const block = !isSet && (isMultiline(row.before) || isMultiline(row.after))

  return (
    <li
      className={cn(
        'grid gap-1 py-2 md:gap-3',
        single ? 'md:grid-cols-[minmax(8rem,1fr)_3fr]' : 'md:grid-cols-[minmax(8rem,1fr)_2fr_2fr]',
      )}
      data-testid={`field-diff-row-${row.path}`}
    >
      <code className="break-all font-mono text-xs text-muted-foreground">{row.path}</code>
      {isSet ? (
        <div className={cn('flex flex-wrap gap-1', !single && 'md:col-span-2')}>
          {(row.added ?? []).map((v) => (
            <Badge key={`+${formatValue(v)}`} variant="success" className="font-mono">
              + {formatValue(v)}
            </Badge>
          ))}
          {(row.removed ?? []).map((v) => (
            <Badge key={`-${formatValue(v)}`} variant="destructive" className="font-mono">
              − {formatValue(v)}
            </Badge>
          ))}
        </div>
      ) : block ? (
        <div className={cn(!single && 'md:col-span-2')}>
          {single ? (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-muted/50 p-2 font-mono text-xs">
              {blockText(row.after)}
            </pre>
          ) : (
            <LineDiff before={blockText(row.before)} after={blockText(row.after)} />
          )}
        </div>
      ) : (
        <>
          {!single && <Value label={beforeLabel} value={row.before} tone="before" />}
          <Value label={afterLabel} value={row.after} tone={single ? 'plain' : 'after'} />
        </>
      )}
    </li>
  )
}

function Value({ label, value, tone }: { label?: string; value: unknown; tone: 'before' | 'after' | 'plain' }) {
  return (
    <div className="min-w-0">
      {label && <span className="text-xs text-muted-foreground md:hidden">{label}: </span>}
      <span
        className={cn(
          'break-words',
          tone === 'before' && 'text-muted-foreground line-through decoration-destructive/60',
          tone === 'after' && 'font-medium',
          (value === null || value === undefined) && 'text-muted-foreground no-underline',
        )}
      >
        {formatValue(value)}
      </span>
    </div>
  )
}

/** A line diff with the `diff` package, as the page version history does. */
function LineDiff({ before, after }: { before: string; after: string }) {
  const parts = useMemo(() => diffLines(before, after), [before, after])
  return (
    <pre className="max-h-72 overflow-auto rounded bg-muted/50 p-2 font-mono text-xs" data-testid="field-diff-lines">
      {parts.flatMap((part, i) => {
        const lines = part.value.split('\n')
        if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop()
        return lines.map((line, j) => (
          <div
            key={`${i}-${j}`}
            className={cn(
              'whitespace-pre-wrap break-all',
              part.added && 'bg-success/10 text-success',
              part.removed && 'bg-destructive/10 text-destructive',
            )}
          >
            {part.added ? '+ ' : part.removed ? '− ' : '  '}
            {line}
          </div>
        ))
      })}
    </pre>
  )
}
